// ───────────────────────────── src/pages/Blogs.jsx ─────────────────────────────
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import "./Blogs.css";

function toJSDate(ts) {
  try {
    return ts?.toDate ? ts.toDate() : ts instanceof Date ? ts : null;
  } catch { return null; }
}

function BlockRenderer({ block }) {
  if (!block) return null;

  // Heading
  if (block.type === "heading") {
    return <h2 className="wbb-h2">{block.text}</h2>;
  }

  // Paragraph (HTML from Quill)
  if (block.type === "paragraph") {
    return <div className="wbb-p" dangerouslySetInnerHTML={{ __html: block.html || "" }} />;
  }

  // List
  if (block.type === "list") {
    const isOrdered = block.style === "ordered";
    const ListTag = isOrdered ? "ol" : "ul";
    return (
      <ListTag className={`wbb-list ${isOrdered ? "wbb-ol" : "wbb-ul"}`}>
        {(block.items || []).map((it, i) => <li key={i}>{it}</li>)}
      </ListTag>
    );
  }

  // Image
  if (block.type === "image") {
    return (
      <figure className="wbb-figure">
        <img src={block.url} alt={block.caption || "Blog image"} />
        {block.caption ? <figcaption>{block.caption}</figcaption> : null}
      </figure>
    );
  }

  // Quote
  if (block.type === "quote") {
    return <blockquote className="wbb-quote">{block.text}</blockquote>;
  }

  return null;
}

export default function Blogs() {
  const { slugOrId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        // Try by doc id first
        const byId = await getDoc(doc(db, "blogs", slugOrId));
        if (byId.exists()) {
          setBlog({ id: byId.id, ...byId.data() });
          return;
        }
        // Fallback by slug
        const q = query(collection(db, "blogs"), where("slug", "==", slugOrId), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          setBlog({ id: d.id, ...d.data() });
        }
      } catch (e) {
        console.error("Failed to load blog:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [slugOrId]);

  if (loading) return <div className="wbb-loading">Loading…</div>;
  if (!blog) return <div className="wbb-loading">Blog not found.</div>;

  const date = toJSDate(blog.publishedAt || blog.createdAt);
  const dateStr = date ? date.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="wbb-page">
      {/* Big hero with main image */}
      <header className="wbb-hero">
        <div className="wbb-hero-media">
          <img
            src={blog.mainPhotoURL || "https://picsum.photos/seed/wbblog/1600/900"}
            alt={blog.title}
          />
        </div>
        <div className="wbb-hero-content">
          <p className="wbb-date">{dateStr}</p>
          <h1 className="wbb-title">{blog.title}</h1>
          <div className="wbb-meta">
            {blog.category ? <span className="wbb-chip">{blog.category}</span> : null}
            {blog.author ? <span className="wbb-chip">By {blog.author}</span> : null}
          </div>
          <button className="wbb-back" onClick={() => navigate("/blogs")}>← Back to Blogs</button>
        </div>
      </header>

      {/* Content */}
      <main className="wbb-content">
        {(Array.isArray(blog.body) ? blog.body : []).map((block, i) => (
          <BlockRenderer block={block} key={i} />
        ))}
      </main>
    </div>
  );
}
// ─────────────────────────────── End of Blogs.jsx ───────────────────────────────
